import React, {useEffect, useMemo, useState} from "react";
import {
    GearCategory,
    GearItem,
    GearWeightMeasurement,
    Trip,
    TripDetails,
    useMealGroups,
    useUpdateTrip
} from "../../api";
import {Button, List, ListItem, ListItemText, TextField} from "@mui/material";
import {MenuEditor} from "./MenuEditor";
import {TripViewCard} from "./TripViewCard";


function WeightDisplay(props: {title: string, weight_oz: number, action?: any, color?: string}) {
    if (isNaN(props.weight_oz)) {
        props.weight_oz = 0.0;
    }
    const sub = <span><b>{(props.weight_oz / 16.0).toFixed(2)} Lbs.</b> - {props.weight_oz.toFixed(2)} oz</span>
    return <ListItem secondaryAction={props.action}>
        <ListItemText primary={props.title}
                      secondary={sub}
                      primaryTypographyProps={{
                          color: props.color ? props.color : "default"
        }}/>
    </ListItem>
}

function EditableWeightDisplay(props: {title: string, weight_oz: number, onChange(change: number): void}) {
    const [weight_string, set_weight_string] = useState(props.weight_oz.toString());
    return <WeightDisplay title={props.title}
                          weight_oz={props.weight_oz}
                          action={<TextField label={`${props.title} (oz)`}
                                              // type="number"
                                              variant="standard"
                                              error={isNaN(Number(weight_string))}
                                              value={weight_string}
                                              onChange={(e) => set_weight_string(e.target.value)}
                                              onBlur={(e) => {
                                                  const as_num = Number(e.target.value);
                                                  if (!isNaN(as_num)) {
                                                      set_weight_string(e.target.value);
                                                      props.onChange(as_num);
                                                  } else {
                                                      set_weight_string(props.weight_oz.toString());
                                                  }
                                              }}/>
    }/>
}

interface TripWeightBreakdownProps {
    trip_details: TripDetails;
    gear_categories: Record<number, GearCategory>;
    gear: Record<number, GearItem>;
    gear_weights: Record<number, GearWeightMeasurement>;
    update_trip(trip_details: TripDetails): void;
}

export function TripWeightBreakdown(props: TripWeightBreakdownProps) {
    const update_trip = useUpdateTrip()[1];
    const meal_groups = useMealGroups()[0];
    const [is_menu_open, set_menu_open] = useState(false);
    const [water_weight, set_water_weight] = useState(0.0);
    const [fuel_weight, set_fuel_weight] = useState(0.0);

    useEffect(() => {
        set_water_weight(props.trip_details.trip.water_weight);
        set_fuel_weight(props.trip_details.trip.fuel_weight);
    }, [props.trip_details]);

    const [base_weight, base_wear_weight, food_weight, is_missing_measurement] = useMemo(() => {
        let base_weight = 0.0;
        let base_wear_weight = 0.0;
        let food_weight = 0.0;
        let is_missing_measurement = false;
        for (const category of (props.trip_details?.gear || [])) {
            if (category.gear_id === undefined || category.gear_id === null) {
                // Don't need to do anything here.
            } else if (props.gear_weights.hasOwnProperty(category.gear_id)) {
                const gear_weight = props.gear_weights[category.gear_id].weight
                // const gear = props.gear[category.gear_id];
                if (category.weight_category === 'pack') {
                    base_weight += gear_weight;
                } else if (category.weight_category === 'carry') {
                    base_wear_weight += gear_weight;
                }
            } else {
                is_missing_measurement = true;
                console.warn("Missing weight measurement for gear_id: " + category.gear_id);
            }
        }

        for (const meal_entry of (props.trip_details?.menu || [])) {
            food_weight += meal_entry.weight;
        }
        return [base_weight, base_wear_weight, food_weight, is_missing_measurement]
    }, [props.trip_details, props.gear_weights]);

    const total = base_weight + food_weight + water_weight + fuel_weight;

    function on_update_trip_response(response: Trip | undefined) {
        if (response) {
            props.update_trip({...props.trip_details, trip: response});
        }
    }

    return <TripViewCard flexGrow={2} minWidth={400}>
        <List>
            <WeightDisplay title="Base weight"
                           weight_oz={base_weight}
                           color={is_missing_measurement ? "red" : "default"}/>
            <WeightDisplay title="Base weight with wear"
                           weight_oz={base_wear_weight + base_weight}
                           color={is_missing_measurement ? "red" : "default"}/>
            <WeightDisplay title="Food"
                           weight_oz={food_weight}
                           action={<Button onClick={() => set_menu_open(true)}>
                               Open Menu
                           </Button>}/>
            <EditableWeightDisplay title="Fuel"
                                   weight_oz={props.trip_details.trip.fuel_weight}
                                   onChange={w => {
                                       update_trip({...props.trip_details.trip, fuel_weight: w}, on_update_trip_response);
                                   }}/>
            <EditableWeightDisplay title="Water"
                                   weight_oz={props.trip_details.trip.water_weight}
                                   onChange={w => {
                                       update_trip({...props.trip_details.trip, water_weight: w}, on_update_trip_response)
                                   }}/>
            <WeightDisplay title="Total"
                           color={is_missing_measurement ? "red" : "default"}
                           weight_oz={total}/>
            <WeightDisplay title="Total Skin-out"
                           color={is_missing_measurement ? "red" : "default"}
                           weight_oz={total + base_wear_weight}/>
        </List>
        <MenuEditor trip_details={props.trip_details}
                    meal_groups={meal_groups || []}
                    is_open={is_menu_open}
                    onClose={() => set_menu_open(false)}
                    onUpdateMealPlan={(meal_plan) => props.update_trip({...props.trip_details, menu: meal_plan})}/>
    </TripViewCard>
}